import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputField } from '@/components/forms/form-input-field';
import { useToast } from '@/components/ui/hooks/use-toast';
import { useRequiredOnboardingLessonsData } from '@/lib/api-call-hooks';
import { groupStudentsAndCreateRegularSessions } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { getOnboardingSessionConfig, instantIntervalsFromClassTime, MultiMap } from '@airreading/common';
import { StudentOfSchool } from '@airreading/common/data';
import { ReactNode, useEffect, useMemo } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { CoachAssignedProps } from '../$schoolId/students';

export interface GenerateMakeupDiagnosticSessionsProps {
  students: StudentOfSchool[];
  orgId: string;
  trigger: ReactNode;
  onSuccess?: () => void;
  onCoachesAssigned?: (props: CoachAssignedProps) => void;
}

interface MakeFormSchemaProps {
  students: StudentOfSchool[];
  maxSessionsByRange: number;
  groupSize: number;
}

function makeFormSchema({ students, maxSessionsByRange, groupSize }: MakeFormSchemaProps) {
  return z
    .object({
      groupSize: z.coerce
        .number()
        .int()
        .min(1, { message: 'Group size must be at least 1' })
        .max(groupSize, {
          message: `Group size must be at most ${groupSize}`,
        }),
      autoCreateSessions: z.boolean(),
      startPlainDate: z.instanceof(Temporal.PlainDate),
      endPlainDate: z.instanceof(Temporal.PlainDate),
    })
    .superRefine((data, ctx) => {
      if (Temporal.PlainDate.compare(data.startPlainDate, data.endPlainDate) > 0) {
        ctx.addIssue({
          path: ['startPlainDate'],
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date.',
        });
        ctx.addIssue({
          path: ['endPlainDate'],
          code: z.ZodIssueCode.custom,
          message: 'End date must be after start date.',
        });
      }

      const numSessionsByRange = getNumberSessionsByRange(students, data.startPlainDate, data.endPlainDate);

      if (maxSessionsByRange && numSessionsByRange > maxSessionsByRange) {
        ctx.addIssue({
          path: ['endPlainDate'],
          code: z.ZodIssueCode.custom,
          message: `Error: Number of sessions (${numSessionsByRange}) over maximum limit (${maxSessionsByRange}).`,
        });
      }
    });
}

export const GenerateMakeupDiagnosticSessions = ({
  students,
  orgId,
  trigger,
  onSuccess,
  onCoachesAssigned,
}: GenerateMakeupDiagnosticSessionsProps) => {
  const classTimes = MultiMap.groupBy(students, (s) => s.requestedClassTime?.id || 'empty');
  const invalidSchedule = classTimes.size > 1 || classTimes.has('empty');
  const {
    lessonName,
    learningPathName,
    groupSize,
    maxSessionsByRange = 100,
  } = getOnboardingSessionConfig('makeup_diagnostic');
  const { startingLesson, learningPath, missingPredefinedLessons, isLoading } = useRequiredOnboardingLessonsData(
    lessonName,
    learningPathName,
  );

  const formSchema = useMemo(
    () => makeFormSchema({ students, maxSessionsByRange, groupSize }),
    [students, maxSessionsByRange, groupSize],
  );

  const defaultValues: DefaultValues<z.infer<typeof formSchema>> = {
    groupSize,
    autoCreateSessions: false,
    startPlainDate: undefined,
    endPlainDate: undefined,
  };
  const { toast } = useToast();

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { groupSize, autoCreateSessions, startPlainDate, endPlainDate } = formData;
    const classTimeId = classTimes.keys().next().value;
    if (!classTimeId) {
      toast({
        title: 'Error generating makeup diagnostic sessions',
        description: 'All selected students must have assigned and the same class time.',
        variant: 'destructive',
      });
      return;
    }
    if (!learningPath || !startingLesson) {
      toast({
        title: 'Error creating makeup diagnostic sessions',
        description: 'Missing predefined lesson or learning path for makeup diagnostics.',
        variant: 'destructive',
      });
      return;
    }

    const numSessionsByRange = getNumberSessionsByRange(students, startPlainDate, endPlainDate);

    if (maxSessionsByRange && numSessionsByRange > maxSessionsByRange) {
      toast({
        title: `Too many sessions`,
        description: `The selected date range would generate ${numSessionsByRange} sessions, but the maximum allowed is ${maxSessionsByRange}. Please adjust the date range.`,
        variant: 'destructive',
      });
      return;
    }

    return groupStudentsAndCreateRegularSessions({
      studentIds: students.map((s) => s.id),
      classTimeId,
      groupSize,
      autoCreateSessions,
      overrideStartPlainDate: startPlainDate,
      overrideEndPlainDate: endPlainDate,
      overrideStartingLevel: {
        learningPathId: learningPath.id,
        startingLessonId: startingLesson.id,
      },
      orgId,
    })
      .then(({ studentGroupsCount, sessionsCount, availableCoachesCount, coachesAssignedCount }) => {
        toast({
          title: `Generated makeup diagnostic sessions for ${studentGroupsCount} student groups and ${sessionsCount} sessions`,
        });
        onSuccess?.();
        closeModal();

        if (studentGroupsCount > 0) {
          onCoachesAssigned?.({ availableCoachesCount, coachesAssignedCount });
        }
      })
      .catch((e) => {
        toast({
          title: 'Error generating makeup diagnostic sessions',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      formSchema={formSchema}
      trigger={trigger}
      modalTitle="Generate makeup diagnostic classes"
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      submitButtonProps={{
        disabled: isLoading || invalidSchedule || missingPredefinedLessons,
      }}
    >
      {({ form }) => {
        if (invalidSchedule) {
          return (
            <div className="mt-4">
              All selected students must have the same class time assigned to be grouped together. Some selections
              either do not have a requested class time assigned, or they are from different class times. Use the table
              filter to select students with the same class time before generating sessions.
            </div>
          );
        }

        const { watch, trigger } = form;
        const [startPlainDate, endPlainDate] = watch(['startPlainDate', 'endPlainDate']);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (!startPlainDate || !endPlainDate) {
            return;
          }
          trigger(['startPlainDate', 'endPlainDate']);
        }, [startPlainDate, endPlainDate, trigger]);

        return (
          <div className="mt-4">
            <p>
              This action will group all selected students up to the selected group size and generate makeup diagnostic
              sessions for them. Specify the start and end date for the sessions. To create student groups only without
              sessions, uncheck the "Auto-create sessions after creating classes" option.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <FormInputField name="groupSize" label="Group size" form={form} />
              </div>
              <FormInputDate name="startPlainDate" label="Start date" form={form} />
              <FormInputDate name="endPlainDate" label="End date" form={form} />
              <FormCheckbox name="autoCreateSessions" label="Auto-create sessions after creating classes" form={form} />
            </div>
          </div>
        );
      }}
    </FormModal>
  );
};

function getNumberSessionsByRange(
  students: StudentOfSchool[],
  startPlainDate: Temporal.PlainDate,
  endPlainDate: Temporal.PlainDate,
): number {
  const classTime = students[0]?.requestedClassTime;

  if (!classTime) {
    return 0;
  }

  return Array.from(
    instantIntervalsFromClassTime({
      classTime,
      overrideStartPlainDate: startPlainDate,
      overrideEndPlainDate: endPlainDate,
    }),
  ).length;
}
